package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.Composable
import com.siriusxm.pia.rest.unifiedaggregator.FieldDiff
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Displays a list of field change rows with shared styles applied once.
 *
 * Callers should use this instead of iterating [DiffFieldChangeRow] directly,
 * as it ensures [EntityDiffStyles] is applied exactly once.
 *
 * @param diffs The field differences to display
 */
@Composable
internal fun DiffFieldChangeList(diffs: List<FieldDiff>) {
    Style(EntityDiffStyles)

    Div({
        style {
            fontSize(13.px)
        }
    }) {
        for (diff in diffs) {
            DiffFieldChangeRow(diff)
        }
    }
}

/**
 * Displays a single field change row showing the field path,
 * old value (struck through in red), an arrow separator, and the new value (in green).
 *
 * Shared by EntityDiffView and AuditHistory. Callers are responsible for providing
 * display-ready paths (use [formatFieldName] to convert camelCase to human-readable form).
 *
 * @param diff The field difference to display
 */
@Composable
private fun DiffFieldChangeRow(diff: FieldDiff) {
    Div({
        classes(EntityDiffStyles.diffRow)
    }) {
        // Field path
        Span({
            classes(EntityDiffStyles.diffFieldPath)
        }) {
            Text(diff.path.ifEmpty { "(root)" })
        }

        // Old value - struck through in red if it exists
        val oldValue = diff.oldValue
        if (oldValue != null) {
            Span({
                classes(EntityDiffStyles.diffOldValue)
            }) {
                Text(formatJsonValue(oldValue))
            }
        } else {
            Span({
                classes(EntityDiffStyles.diffNullValue)
            }) {
                Text("(not set)")
            }
        }

        // Arrow separator
        Span({
            classes(EntityDiffStyles.diffArrow)
        }) {
            Text("\u2192")
        }

        // New value - shown in green
        val newValue = diff.newValue
        if (newValue != null) {
            Span({
                classes(EntityDiffStyles.diffNewValue)
            }) {
                Text(formatJsonValue(newValue))
            }
        } else {
            Span({
                classes(EntityDiffStyles.diffNullValue)
            }) {
                Text("(removed)")
            }
        }
    }
}

/**
 * Formats a JSON value for display in a concise, readable format.
 *
 * - Primitives are shown as-is
 * - Small arrays (<=3 primitive elements) are shown inline
 * - Larger arrays show "[N items]"
 * - Objects show "{N fields}"
 *
 * @param value The JSON element to format
 * @return A string representation of the value
 */
internal fun formatJsonValue(value: JsonElement): String {
    return when (value) {
        is JsonPrimitive -> value.contentOrNull ?: value.toString()
        is JsonArray -> {
            if (value.isEmpty()) {
                "[]"
            } else if (value.size <= 3 && value.all { it is JsonPrimitive }) {
                "[${value.joinToString(", ") { formatJsonValue(it) }}]"
            } else {
                "[${value.size} items]"
            }
        }
        is JsonObject -> {
            if (value.isEmpty()) {
                "{}"
            } else {
                "{${value.size} fields}"
            }
        }
    }
}

/**
 * Formats a field name from camelCase to human-readable form.
 * e.g. "entityType" becomes "Entity Type"
 */
internal fun formatFieldName(name: String): String {
    return name.replace(Regex("([a-z])([A-Z])"), "$1 $2")
        .replaceFirstChar { it.uppercase() }
}
